import { DatePicker } from "@mui/x-date-pickers";
import { Controller, useFormContext } from "react-hook-form";
import { FormKeys } from "../../helpers";
import { useEffect, useState } from "react";
import { Checkbox, FormControlLabel } from "@mui/material";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

type Props = {
  isReadOnly: boolean;
  isPeriodDisabledWhenNoDate: boolean;
};

export const ProbationaryPeriod = ({ isReadOnly, isPeriodDisabledWhenNoDate }: Props) => {
  const { t } = useTranslation();
  const { workAgreementId } = useParams();

  const { control, setValue, formState, watch, clearErrors } = useFormContext();
  const { probationEndDate, workStarts, workEnds } = watch();

  const [isProbationaryPeriodDisabled, setIsProbationaryPeriodDisabled] =
    useState<boolean>(isPeriodDisabledWhenNoDate && !probationEndDate);

  // Form state updates with existing work agreement values after initial values, so need to wait for that
  useEffect(() => {
    // Don't do anything for new offers or if user has started filling the form
    if (formState.isDirty || !workAgreementId) return;
    // If existing offer has probation end date set, probation period is enabled
    setIsProbationaryPeriodDisabled(!probationEndDate);
  }, [probationEndDate, formState.isDirty, workAgreementId]);

  const handleToggle = () => {
    const newValue = !isProbationaryPeriodDisabled;
    setIsProbationaryPeriodDisabled(newValue);
    if (newValue === true) {
      setValue(FormKeys.ProbationEndDate, null);
      clearErrors(FormKeys.ProbationEndDate);
    }
  };

  return (
    <>
      <Controller
        control={control}
        name={FormKeys.ProbationEndDate}
        rules={{
          required: !isProbationaryPeriodDisabled,
          validate: (date) => {
            if (isProbationaryPeriodDisabled) return true;
            const workStartsDate = workStarts;
            workStartsDate.setHours(0, 0, 0, 0);
            const validStart =
              workStartsDate && date.getTime() >= workStartsDate.getTime();
            const workEndsDate = workEnds;
            workEndsDate && workEndsDate.setHours(0, 0, 0, 0);
            const validEnd = workEndsDate
              ? date.getTime() <= workEndsDate.getTime()
              : true;
            return validStart && validEnd;
          },
        }}
        render={({ field }) => (
          <DatePicker
            {...field}
            disabled={isProbationaryPeriodDisabled || isReadOnly}
            slotProps={{
              textField: {
                size: "small",
                label: null,
                error: !!formState.errors[FormKeys.ProbationEndDate],
              },
            }}
            sx={{
              width: "150px",
            }}
          />
        )}
      />
      <FormControlLabel
        sx={{ marginLeft: "10px" }}
        control={
          <Checkbox
            checked={isProbationaryPeriodDisabled}
            value={isProbationaryPeriodDisabled}
            onChange={handleToggle}
            disabled={isReadOnly}
          />
        }
        label={t("order:noProbationaryPeriod")}
      />
    </>
  );
};
